import { StringDevUIField } from "./StringDevUIField";
import { resolveDevtoolsIcon } from "../../StringDevIconRegistry";

const HOLD_DELAY_MS = 320;
const HOLD_INTERVAL_MS = 60;
const DRAG_PIXELS_PER_STEP = 6;

export class StringDevFieldRange extends StringDevUIField<number> {
  private min: number;
  private max: number;
  private step: number;
  private displayMultiplier: number;
  private displayStep?: number;
  private suffix?: string;
  private units: ReadonlyArray<{ value: string; label: string }> | undefined;
  private currentUnit: string | undefined;
  private valueInput: HTMLInputElement | null = null;
  private unitSelect: HTMLSelectElement | null = null;
  private stepperButtons: HTMLButtonElement[] = [];

  private static readonly DECIMAL_UNITS = new Set(["rem", "em"]);

  constructor(
    key: string,
    label: string,
    defaultValue: number,
    onChange: (key: string, value: any) => void,
    min: number = 0,
    max: number = 100,
    step: number = 1,
    displayMultiplier: number = 1,
    displayStep?: number,
    suffix?: string,
    units?: ReadonlyArray<{ value: string; label: string }>,
    currentUnit?: string,
  ) {
    super(key, label, defaultValue, onChange);
    this.min = min;
    this.max = max;
    this.step = step;
    this.displayMultiplier = displayMultiplier;
    this.displayStep = displayStep;
    this.suffix = suffix;
    this.units = units;
    this.currentUnit = currentUnit;
  }

  build(): HTMLElement {
    const row = this.createRow();
    row.appendChild(this.createLabel());
    row.appendChild(this.createControls());
    this.root = row;
    return row;
  }

  setValue(value: number): void {
    this.value = value;
    if (this.valueInput) this.valueInput.value = this.formatValue(value);
  }

  override setDisabled(disabled: boolean): void {
    super.setDisabled(disabled);
    if (this.valueInput) this.valueInput.disabled = disabled;
    if (this.unitSelect) this.unitSelect.disabled = disabled;
    for (const button of this.stepperButtons) {
      button.disabled = disabled;
    }
  }

  private createControls(): HTMLDivElement {
    const control = document.createElement("div");
    control.setAttribute("data-stdg-field-input", "");
    const inputShell = document.createElement("div");
    inputShell.setAttribute("data-stdg-field-value", "");
    inputShell.appendChild(this.createInput());

    if (this.suffix) {
      const suffix = document.createElement("span");
      suffix.setAttribute("data-stdg-field-suffix", "");
      suffix.textContent = this.suffix;
      inputShell.appendChild(suffix);
    }

    control.appendChild(inputShell);

    if (this.units && this.units.length > 0) {
      const unitSelect = document.createElement("select");
      unitSelect.setAttribute("data-stdg-select", "");

      for (const u of this.units) {
        const opt = document.createElement("option");
        opt.value = u.value;
        opt.textContent = u.label;
        unitSelect.appendChild(opt);
      }

      unitSelect.value = this.currentUnit || this.units[0].value;
      unitSelect.addEventListener("change", () => {
        this.currentUnit = unitSelect.value;
        if (this.valueInput) this.valueInput.value = this.formatValue(this.value);
        this.onChange(this.key + "Unit", unitSelect.value);
      });

      this.unitSelect = unitSelect;
      control.appendChild(unitSelect);
    }

    const stepper = document.createElement("div");
    stepper.setAttribute("data-stdg-stepper", "");
    stepper.appendChild(this.createStepperButton("up", 1));
    stepper.appendChild(this.createStepperButton("down", -1));
    control.appendChild(stepper);

    return control;
  }

  private createInput(): HTMLInputElement {
    const valueInput = document.createElement("input");
    valueInput.type = "text";
    valueInput.inputMode = "text";
    valueInput.setAttribute("enterkeyhint", "done");
    valueInput.autocomplete = "off";
    valueInput.spellcheck = false;
    valueInput.value = this.formatValue(this.value);
    valueInput.setAttribute("data-stdg-input", "");

    valueInput.addEventListener("focus", () => {
      valueInput.select();
    });
    valueInput.addEventListener("blur", () => {
      this.commitInputValue(valueInput);
    });
    valueInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        valueInput.blur();
      } else if (e.key === "Escape") {
        valueInput.value = this.formatValue(this.value);
        valueInput.blur();
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        this.applyDelta(e.key === "ArrowUp" ? 1 : -1, e, valueInput);
      }
    });
    valueInput.addEventListener("wheel", (event) => {
      if (document.activeElement !== valueInput) return;
      event.preventDefault();
      this.applyDelta(event.deltaY < 0 ? 1 : -1, event, valueInput);
    });
    valueInput.addEventListener("mousedown", (event) => {
      if (event.button !== 1) return;
      this.startMiddleDrag(event, valueInput);
    });
    valueInput.addEventListener("auxclick", (event) => {
      if (event.button === 1) event.preventDefault();
    });

    this.valueInput = valueInput;
    return valueInput;
  }

  private createStepperButton(directionName: "up" | "down", direction: 1 | -1): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-stdg-button", "");
    button.setAttribute("data-stdg-button-icon-12", "");
    button.setAttribute("data-direction", directionName);
    button.innerHTML = resolveDevtoolsIcon(
      12,
      directionName === "up" ? "chevrone-up" : "chevrone-down",
    );

    let holdTimeout = 0;
    let holdInterval = 0;
    let suppressClick = false;

    const stopHold = () => {
      window.clearTimeout(holdTimeout);
      window.clearInterval(holdInterval);
      document.removeEventListener("pointerup", stopHold);
      document.removeEventListener("pointercancel", stopHold);
    };

    button.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;

      holdTimeout = window.setTimeout(() => {
        suppressClick = true;
        if (!this.valueInput) return;
        this.applyDelta(direction, undefined, this.valueInput);
        holdInterval = window.setInterval(() => {
          if (!this.valueInput) return;
          this.applyDelta(direction, undefined, this.valueInput);
        }, HOLD_INTERVAL_MS);
      }, HOLD_DELAY_MS);

      document.addEventListener("pointerup", stopHold);
      document.addEventListener("pointercancel", stopHold);
    });

    button.addEventListener("click", (event) => {
      if (suppressClick) {
        suppressClick = false;
        event.preventDefault();
        return;
      }

      if (!this.valueInput) return;
      this.applyDelta(direction, undefined, this.valueInput);
      this.valueInput.focus();
      this.valueInput.select();
    });

    this.stepperButtons.push(button);
    return button;
  }

  private startMiddleDrag(event: MouseEvent, input: HTMLInputElement): void {
    event.preventDefault();

    let previousX = event.clientX;
    let accumulated = 0;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";

    const stopDrag = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", stopDrag);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - previousX;
      previousX = moveEvent.clientX;
      accumulated += deltaX;

      while (Math.abs(accumulated) >= DRAG_PIXELS_PER_STEP) {
        const direction = accumulated > 0 ? 1 : -1;
        this.applyDelta(direction, moveEvent, input);
        accumulated -= DRAG_PIXELS_PER_STEP * direction;
      }
    };

    input.focus();
    input.select();
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", stopDrag);
  }

  private commitInputValue(input: HTMLInputElement): void {
    const parsed = parseFloat(input.value.replace(",", "."));
    if (isNaN(parsed)) {
      input.value = this.formatValue(this.value);
      return;
    }
    const snapped = this.normalizeDisplayValue(parsed);
    input.value = this.formatValue(snapped);
    this.emit(snapped);
  }

  private applyDelta(
    direction: 1 | -1,
    event: KeyboardEvent | WheelEvent | MouseEvent | undefined,
    input: HTMLInputElement,
  ): void {
    const multiplier = event?.shiftKey ? 10 : event?.altKey ? 0.1 : 1;
    const displayNextValue =
      this.modelToDisplay(this.value) + direction * this.getDisplayStep() * multiplier;
    const nextValue = this.normalizeDisplayValue(displayNextValue);
    input.value = this.formatValue(nextValue);
    this.emit(nextValue);
  }

  private normalizeValue(value: number): number {
    const clamped = Math.min(this.max, Math.max(this.min, value));
    if (StringDevFieldRange.DECIMAL_UNITS.has(this.currentUnit ?? "")) {
      return Number(clamped.toFixed(2));
    }
    const snapped = Math.round(clamped / this.step) * this.step;
    const precision = this.getPrecision(this.step);
    return Number(snapped.toFixed(precision));
  }

  private normalizeDisplayValue(value: number): number {
    const displayMin = this.modelToDisplay(this.min);
    const displayMax = this.modelToDisplay(this.max);
    const clamped = Math.min(displayMax, Math.max(displayMin, value));
    const precision = this.getDisplayPrecision();
    if (StringDevFieldRange.DECIMAL_UNITS.has(this.currentUnit ?? "")) {
      const normalizedDisplay = Number(clamped.toFixed(precision));
      return this.normalizeValue(this.displayToModel(normalizedDisplay));
    }
    const displayStep = this.getDisplayStep();
    const snapped = Math.round(clamped / displayStep) * displayStep;
    const normalizedDisplay = Number(snapped.toFixed(precision));
    return this.normalizeValue(this.displayToModel(normalizedDisplay));
  }

  private getPrecision(value: number): number {
    const parts = String(value).split(".");
    return parts[1]?.length ?? 0;
  }

  private formatValue(v: number): string {
    const displayValue = this.modelToDisplay(v);
    const precision = this.getDisplayPrecision();
    return precision > 0 ? displayValue.toFixed(precision) : String(displayValue);
  }

  private modelToDisplay(value: number): number {
    const precision = this.getDisplayPrecision();
    return Number((value * this.displayMultiplier).toFixed(precision));
  }

  private displayToModel(value: number): number {
    return value / this.displayMultiplier;
  }

  private getDisplayStep(): number {
    return this.displayStep ?? this.step * this.displayMultiplier;
  }

  private getDisplayPrecision(): number {
    if (StringDevFieldRange.DECIMAL_UNITS.has(this.currentUnit ?? "")) {
      return 2;
    }
    const displayStep = this.getDisplayStep();
    return displayStep < 1 ? Math.max(2, this.getPrecision(displayStep)) : 0;
  }
}
